import React from "react";
import styled from "styled-components";
import omit from "lodash/omit";
import { Container, Col, Row } from "styled-bootstrap-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faClock } from "@fortawesome/free-solid-svg-icons/faClock";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons/faMapMarkerAlt";
import { faCompass } from "@fortawesome/free-solid-svg-icons/faCompass";
import DealersMap from "./DealersMap";
import { DAYS_OF_WEEK } from "./defaultDealer";
import { Dealer } from "../../../types";
import BackLink from "../../common/Link/BackLink";
import { SectionHeaderH1 } from "../../common/Header/SectionHeader";
import Header from "../../common/Header/Header";

type DearlerDetailProps = {
  dealer: Dealer;
};

const SectionContainer = styled.section`
  flex: 1;
  padding-top: 50px;
  padding-bottom: 150px;

  ${SectionHeaderH1} {
    margin: 50px 0;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  background-color: #ffffff;
  border: 4px solid #fec00f;
`;

const DetailContainer = styled.div`
  margin-bottom: 30px;
`;

const DetailHeader = styled.h1`
  font-size: 24px;
  color: #43525a;
  margin: 20px 0;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 22px;
  margin: 10px 0;
`;

const OpeningHour = styled(DetailItem)`
  align-items: start;
`;

const IconContainer = styled.span`
  background-color: #fec00f;
  min-width: 30px;
  height: 30px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 20px;
  color: #43525a;
`;

const BreadCrumbContainer = styled.ul`
  list-style: none;
`;

const BreadCrumbItem = styled.li`
  display: inline;
  margin-right: 10px;
`;

const HourList = styled.ol``;

const HourItem = styled.li`
  font-size: 22px;
  line-height: 28px;
`;

const StyledExternalLink = styled.a`
  text-decoration: none;
  color: #000005;
  font-size: 22px;
`;

const CapitalizedText = styled.span`
  font-size: 22px;
  text-transform: capitalize;
`;

const groupHours = (hours) => {
  let groupedHours = {};
  for (let item in DAYS_OF_WEEK) {
    if (isNaN(Number(item)) && !!hours[item]) {
      const value = hours[item];
      if (!groupedHours[value]) {
        groupedHours[value] = [item];
      } else {
        groupedHours[value] = groupedHours[value].concat([item]);
      }
    }
  }
  return groupedHours;
};

const processedDate = (dates) => {
  const weekString = Object.values(DAYS_OF_WEEK)
    .filter((value) => typeof value === "string")
    .toString();
  const datesString = dates.toString();
  if (weekString.includes(datesString) && dates.length > 2) {
    return [dates[0], dates[dates.length - 1]];
  }
  return dates;
};

export const processedTime = (workingTime) => {
  const arr = workingTime.split("-");
  if (arr.length === 1) {
    return workingTime;
  } else {
    return arr
      .map((item) => {
        const splitedArr = item.split(":");
        if (splitedArr[0] > 12) {
          return (
            item.replace(splitedArr[0], (splitedArr[0] - 12).toString()) + " PM"
          );
        } else {
          return (
            item.replace(splitedArr[0], Number(splitedArr[0]).toString()) +
            " AM"
          );
        }
      })
      .join(" - ");
  }
};

const DealerDefault: React.FC<DearlerDetailProps> = ({ dealer }) => {
  const groupedHours = groupHours(omit(dealer.hours, ["location_id", "name"]));

  const emailReferrer = () => {
    //@ts-ignore
    if (Array.isArray(window.dataLayer)) {
      //@ts-ignore
      window.dataLayer.push({
        event: "email_dealer_from_website",
        storeId: dealer.id,
        dealerName: dealer.name,
      });
    }
  };

  const callPhone = () => {
    //@ts-ignore
    if (Array.isArray(window.dataLayer)) {
      //@ts-ignore
      window.dataLayer.push({
        event: "call_dealer_from_website",
        storeId: dealer.id,
        dealerName: dealer.name,
      });
    }
  };

  return (
    <>
      <Header />
      <SectionContainer>
        <Container>
          <Row>
            <Col col={12}>
              <BreadCrumbContainer>
                <BreadCrumbItem>
                  <BackLink to="/locations/">Locations</BackLink>
                </BreadCrumbItem>
                <BreadCrumbItem>
                  <BackLink to="./">{dealer.name}</BackLink>
                </BreadCrumbItem>
              </BreadCrumbContainer>
            </Col>
          </Row>
          <Row>
            <Col col={12}>
              <SectionHeaderH1>{dealer.name}</SectionHeaderH1>
            </Col>
          </Row>
          <Row>
            <Col col={12} lg={6} order={2} lgOrder={1}>
              <DetailContainer>
                <DetailHeader>Location</DetailHeader>
                <DetailItem>
                  <IconContainer>
                    <FontAwesomeIcon icon={faMapMarkerAlt} size="1x" />
                  </IconContainer>
                  {dealer.address_line_1}
                </DetailItem>
                <DetailItem>
                  <IconContainer>
                    <FontAwesomeIcon icon={faCompass} size="1x" />
                  </IconContainer>
                  {`${dealer.city}, ${dealer.state} ${dealer.postcode}`}
                </DetailItem>
                {Object.keys(groupedHours).length > 0 && (
                  <OpeningHour>
                    <IconContainer>
                      <FontAwesomeIcon icon={faClock} size="1x" />
                    </IconContainer>
                    <HourList>
                      {Object.keys(groupedHours).map((key) => (
                        <HourItem>
                          {processedDate(groupedHours[key]).map(
                            (date, index) => (
                              <>
                                <CapitalizedText>{date}</CapitalizedText>
                                <CapitalizedText>
                                  {index ===
                                  processedDate(groupedHours[key]).length - 1
                                    ? ""
                                    : " - "}
                                </CapitalizedText>
                              </>
                            )
                          )}
                          {" : "}
                          <CapitalizedText>
                            {processedTime(key)}
                          </CapitalizedText>
                        </HourItem>
                      ))}
                    </HourList>
                  </OpeningHour>
                )}
              </DetailContainer>
              <DetailContainer>
                <DetailHeader>Contact</DetailHeader>
                {dealer.phone && (
                  <DetailItem onClick={callPhone}>
                    <IconContainer>
                      <FontAwesomeIcon icon={faPhone} size="1x" />
                    </IconContainer>
                    <StyledExternalLink href={`tel:${dealer.phone}`}>
                      {dealer.phone}
                    </StyledExternalLink>
                  </DetailItem>
                )}
                {dealer.email && (
                  <DetailItem onClick={emailReferrer}>
                    <IconContainer>
                      <FontAwesomeIcon icon={faEnvelope} size="1x" />
                    </IconContainer>

                    <StyledExternalLink href={"mailto:" + dealer.email}>
                      {dealer.email}
                    </StyledExternalLink>
                  </DetailItem>
                )}
              </DetailContainer>
            </Col>
            <Col col={12} lg={6} order={1} lgOrder={2}>
              <MapContainer>
                <DealersMap lat={Number(dealer.lat)} lng={Number(dealer.lng)} />
              </MapContainer>
            </Col>
          </Row>
        </Container>
      </SectionContainer>
    </>
  );
};

export default DealerDefault;
